import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { warungApi } from '@/services/warungApi'
import { ApiError } from '@/services/http'
import { createIdempotencyAttempt } from '@/services/idempotency'
import { clearWarungToken, readWarungToken, saveWarungToken } from '@/services/warungToken'
import type { PaginationMeta, ProductListQuery, PublicGoogleRegistrationInput, PublicRegistrationInput, ProductInput, WarungDashboard, WarungPermission, WarungProduct, WarungSubscription, WarungUser } from '@/types/warung'

export const useWarungStore = defineStore('warung-workspace', () => {
  const productCreateAttempt = createIdempotencyAttempt('product')
  const productUpdateAttempt = createIdempotencyAttempt('product-update')
  const productArchiveAttempt = createIdempotencyAttempt('product-archive')
  const productRestoreAttempt = createIdempotencyAttempt('product-restore')
  const authInitialized = ref(false)
  const currentUser = ref<WarungUser | null>(null)
  const dashboard = ref<WarungDashboard | null>(null)
  const products = ref<readonly WarungProduct[]>([])
  const productsMeta = ref<PaginationMeta>({ page: 1, perPage: 20, total: 0, totalPages: 1 })
  const productQuery = ref<ProductListQuery>({ page: 1, perPage: 20, active: 1 })
  const loading = ref(false)
  const error = ref<string | null>(null)
  const subscription = ref<WarungSubscription | null>(null)
  const subscriptionLoadedAt = ref<number | null>(null)

  const authenticated = computed(() => currentUser.value !== null)
  function hasPermission(permission: WarungPermission): boolean { return currentUser.value?.permissions.includes(permission) === true }
  function hasEntitlement(key: string): boolean { return subscription.value?.entitlements[key] === true }
  function planLimit(key: string, fallback = 0): number { return subscription.value?.limits[key] ?? fallback }

  // Kegagalan mengambil subscription tidak boleh dianggap sebagai sesi tidak
  // valid: token dan identitas pengguna tetap sah walau endpoint subscription
  // sedang bermasalah. Percobaan berikutnya ditangani oleh
  // refreshSubscriptionIfStale() saat navigasi berikutnya.
  async function loadSubscriptionQuietly(): Promise<void> {
    try {
      subscription.value = await warungApi.subscription()
      subscriptionLoadedAt.value = Date.now()
    } catch {
      subscription.value = null
      subscriptionLoadedAt.value = null
    }
  }

  async function initializeAuth(): Promise<void> {
    if (authInitialized.value) return
    try {
      if (readWarungToken() === null) {
        currentUser.value = null
        subscription.value = null
        subscriptionLoadedAt.value = null
      } else {
        currentUser.value = await warungApi.me()
        await loadSubscriptionQuietly()
      }
    } catch {
      clearWarungToken()
      currentUser.value = null
      subscription.value = null
      subscriptionLoadedAt.value = null
    } finally {
      authInitialized.value = true
    }
  }

  async function refreshCurrentUser(): Promise<void> {
    if (readWarungToken() === null) { currentUser.value = null; return }
    currentUser.value = await warungApi.me()
    await loadSubscriptionQuietly()
  }

  async function register(input: PublicRegistrationInput): Promise<{ verificationEmailSent: boolean }> {
    loading.value = true
    error.value = null
    try {
      const result = await warungApi.register(input)
      saveWarungToken(result.session.token)
      currentUser.value = result.session.user
      subscription.value = result.subscription
      subscriptionLoadedAt.value = Date.now()
      authInitialized.value = true
      return { verificationEmailSent: result.verificationEmailSent }
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Pendaftaran belum berhasil.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // Bila email/akun Google sudah terdaftar, /public/register/google membalas 409.
  // Kredensial Google yang sama masih sah, jadi alih-alih menampilkan jalan buntu
  // kita langsungkan sebagai "Masuk dengan Google" (sesuai perilaku endpoint login
  // yang otomatis menautkan akun lama). linkedExisting menandai kasus ini agar
  // pemanggil dapat mengarahkan ke beranda, bukan halaman "Mulai".
  async function registerWithGoogle(input: PublicGoogleRegistrationInput): Promise<{ linkedExisting: boolean }> {
    loading.value = true
    error.value = null
    try {
      const result = await warungApi.registerWithGoogle(input)
      saveWarungToken(result.session.token)
      currentUser.value = result.session.user
      subscription.value = result.subscription
      subscriptionLoadedAt.value = Date.now()
      authInitialized.value = true
      return { linkedExisting: false }
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 409) {
        try {
          const session = await warungApi.loginWithGoogle(input.credential)
          saveWarungToken(session.token)
          currentUser.value = session.user
          authInitialized.value = true
          await loadSubscriptionQuietly()
          return { linkedExisting: true }
        } catch (loginError: unknown) {
          error.value = loginError instanceof Error ? loginError.message : 'Pendaftaran dengan Google belum berhasil.'
          throw loginError
        }
      }
      error.value = e instanceof Error ? e.message : 'Pendaftaran dengan Google belum berhasil.'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function login(identifier: string, password: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const session = await warungApi.login({ identifier, password })
      saveWarungToken(session.token)
      currentUser.value = session.user
      authInitialized.value = true
      // Login sudah berhasil di titik ini (token tersimpan, identitas didapat).
      // Jangan gagalkan login hanya karena endpoint subscription bermasalah.
      await loadSubscriptionQuietly()
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Login belum berhasil.'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function loginWithGoogle(credential: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const session = await warungApi.loginWithGoogle(credential)
      saveWarungToken(session.token)
      currentUser.value = session.user
      authInitialized.value = true
      // Login sudah berhasil di titik ini (token tersimpan, identitas didapat).
      // Jangan gagalkan login hanya karena endpoint subscription bermasalah.
      await loadSubscriptionQuietly()
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Masuk dengan Google belum berhasil.'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      if (currentUser.value !== null) await warungApi.logout()
    } catch {
      // token lokal tetap dibersihkan saat server sedang tidak tersedia.
    }
    clearWarungToken()
    currentUser.value = null
    dashboard.value = null
    products.value = []
    productsMeta.value = { page: 1, perPage: 20, total: 0, totalPages: 1 }
    productQuery.value = { page: 1, perPage: 20, active: 1 }
    subscription.value = null
    subscriptionLoadedAt.value = null
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await warungApi.changePassword(currentPassword, newPassword)
    if (currentUser.value !== null) currentUser.value = { ...currentUser.value, mustChangePassword: false }
  }

  async function loadSubscription(): Promise<void> {
    subscription.value = await warungApi.subscription()
    subscriptionLoadedAt.value = Date.now()
  }

  async function refreshSubscriptionIfStale(maxAgeMs = 60_000): Promise<void> {
    const loadedAt = subscriptionLoadedAt.value
    if (subscription.value !== null && loadedAt !== null && Date.now() - loadedAt < maxAgeMs) return
    await loadSubscription()
  }

  async function loadDashboard(): Promise<void> {
    dashboard.value = await warungApi.dashboard()
  }

  async function loadProducts(query: ProductListQuery = productQuery.value): Promise<void> {
    productQuery.value = {
      page: query.page ?? 1,
      perPage: query.perPage ?? 20,
      ...(query.search !== undefined ? { search: query.search } : {}),
      ...(query.active !== undefined ? { active: query.active } : {}),
      ...(query.category !== undefined ? { category: query.category } : {}),
    }
    const result = await warungApi.products(productQuery.value)
    products.value = result.items
    productsMeta.value = result.meta
  }

  async function saveProduct(id: string | null, input: ProductInput): Promise<void> {
    if (id === null) {
      await warungApi.createProduct(input, productCreateAttempt.keyFor(input))
      productCreateAttempt.reset()
    } else {
      await warungApi.updateProduct(id, input, productUpdateAttempt.keyFor({ id, input }))
      productUpdateAttempt.reset()
    }
    await Promise.all([loadProducts(productQuery.value), loadDashboard()])
  }

  async function archiveProduct(id: string): Promise<void> {
    await warungApi.archiveProduct(id, productArchiveAttempt.keyFor({ id, archived: true }))
    productArchiveAttempt.reset()
    await Promise.all([loadProducts(productQuery.value), loadDashboard()])
  }
  async function restoreProduct(id: string): Promise<void> {
    await warungApi.restoreProduct(id, productRestoreAttempt.keyFor({ id, archived: false }))
    productRestoreAttempt.reset()
    await Promise.all([loadProducts(productQuery.value), loadDashboard()])
  }

  return { authInitialized, currentUser, dashboard, products, productsMeta, subscription, loading, error, authenticated, hasPermission, hasEntitlement, planLimit, initializeAuth, refreshCurrentUser, refreshSubscriptionIfStale, register, registerWithGoogle, login, loginWithGoogle, logout, changePassword, loadSubscription, loadDashboard, loadProducts, saveProduct, archiveProduct, restoreProduct }
})
